import { Reveal } from "@/components/Motion";

const scalePos = (n) => ((24 - n) / 22) * 100;

const TICKS = [24, 20, 16, 12, 8, 4, 2];

export default function HatchGrid({ rows, pin }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-[#0D1626] p-5 sm:p-8" data-testid="hatch-grid">
            <div className="hidden md:grid grid-cols-[170px_1fr_210px_110px_1.2fr] gap-4 px-4 pb-3 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 border-b border-white/10">
                <span>Fly Category</span>
                <span>Target Insect / Prey</span>
                <span>Hook Size</span>
                <span>Tippet</span>
                <span>Best Water / Weather</span>
            </div>

            <div className="md:divide-y md:divide-white/5 space-y-4 md:space-y-0">
                {rows.map((row, i) => (
                    <Reveal key={row.category} delay={i * 0.06}>
                        <div
                            data-testid={`hatch-row-${row.category.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className="group relative grid md:grid-cols-[170px_1fr_210px_110px_1.2fr] gap-3 md:gap-4 rounded-xl md:rounded-none bg-[#111C2E] md:bg-transparent p-4 md:py-5 md:pr-12 border border-white/10 md:border-0 hover:bg-[#1A273D] transition-colors duration-300"
                        >
                            <div className="flex items-center gap-3 pr-9 md:pr-0">
                                <span className="h-8 w-1.5 rounded-full shrink-0" style={{ background: row.color }} />
                                <span className="font-serif text-lg font-semibold text-slate-50 leading-tight">{row.category}</span>
                            </div>

                            <p className="font-body text-sm text-slate-400 self-center">{row.target}</p>

                            <div className="self-center">
                                <div className="flex justify-between items-baseline mb-1.5">
                                    <span className="font-mono text-sm font-medium" style={{ color: row.color }}>{row.size}</span>
                                    <span className="font-mono text-[9px] text-slate-600 tracking-wider">#24 → #2</span>
                                </div>
                                <div className="relative h-2 rounded-full bg-white/5 overflow-hidden">
                                    <div
                                        className="absolute inset-y-0 rounded-full transition-all duration-700"
                                        style={{
                                            left: `${scalePos(row.max)}%`,
                                            width: `${scalePos(row.min) - scalePos(row.max)}%`,
                                            background: row.color,
                                            boxShadow: `0 0 12px ${row.color}66`,
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="self-center">
                                <span className="inline-block rounded-lg border border-white/15 bg-[#0B1320] px-3 py-1.5 font-mono text-xs text-slate-200">
                                    {row.tippet}
                                </span>
                            </div>

                            <p className="font-body text-sm text-slate-400 self-center">{row.conditions}</p>

                            {pin && (
                                <a
                                    href={`https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(pin.url)}&media=${encodeURIComponent(pin.media)}&description=${encodeURIComponent(`${row.category}: ${row.target}. Hook sizes ${row.size}, tippet ${row.tippet}. ${pin.title}`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`pin-row-${row.category.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    aria-label={`Pin ${row.category} to Pinterest`}
                                    title={`Pin ${row.category}`}
                                    className="absolute top-3 right-3 md:top-1/2 md:-translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#0B1320]/80 backdrop-blur-md border border-white/15 text-slate-300 md:opacity-0 md:group-hover:opacity-100 hover:bg-[#E60023] hover:border-[#E60023] hover:text-white transition-all duration-300"
                                >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                        <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.739a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
                                    </svg>
                                </a>
                            )}
                        </div>
                    </Reveal>
                ))}
            </div>

            <div className="relative mt-6 mx-4 h-5 hidden md:block">
                {TICKS.map((t) => (
                    <span
                        key={t}
                        className="absolute -translate-x-1/2 font-mono text-[9px] tracking-wider text-slate-600"
                        style={{ left: `${scalePos(t)}%` }}
                    >
                        #{t}
                    </span>
                ))}
            </div>
            <p className="mt-4 md:mt-2 px-4 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-600">
                Smaller number = bigger fly. The bar shows each category's hook-size range on the full spectrum.
            </p>
            <p className="mt-2 px-4 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-600">
                Fluoro = fluorocarbon (sinks, near-invisible) · Copolymer = nylon copolymer (floats higher)
            </p>
        </div>
    );
}
